import { observer } from 'mobx-react-lite';
import {
    CommandBarButton,
    IButton,
    DefaultButton,
    IButtonStyles,
} from '@fluentui/react/lib/Button';
import { DirectionalHint } from '@fluentui/react/lib/ContextualMenu';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { ControlIcons } from 'owa-control-icons';
import * as React from 'react';
import useCalendarHeaderViewState from '../hooks/useCalendarHeaderViewState';
import type { CalendarHeaderProps, CalendarTitleProps } from '../schema';
import * as styles from './styles.scss';
import loc from 'owa-localize';
import { calendarAssetNewEventButtonText } from '../calendar.locstring.json';
import { addCalendarLabel } from 'owa-locstrings/lib/strings/addcalendarlabel.locstring.json';
import { getTitleSection } from '../components/CalendarTitle';

const calloutMaxHeight = 300;
const calloutWidth = 220;
const contextMenuItemHeight = 36;

const commandBarButtonIconProps = {
    iconName: ControlIcons.Calendar,
    styles: { root: styles.calendarPickerIcon },
};

const commandBarButtonMenuProps = {
    directionalHint: DirectionalHint.bottomLeftEdge,
    calloutProps: {
        calloutMaxHeight: calloutMaxHeight,
        calloutWidth: calloutWidth,
        styles: {
            calloutMain: styles.calendarPickerCallout,
        },
    },
};

export default observer(function CalendarHeader(props: CalendarHeaderProps) {
    const pickerComponentRef = React.useRef<IButton | null>(null);
    const {
        pickerLabel,
        pickerItems,
        showSpinner,
        containerRef,
        calendarPickerRef,
        shouldShowNewEventText,
        newEventOnClick,
        newEventButtonAnchorId,
        onWheel,
        shouldShowNewButton,
        title,
        onChangeTitle,
        calendarPickerMaxWidth,
        newEventButtonRef,
        onOpenAddCalendar,
    } = useCalendarHeaderViewState(props, pickerComponentRef);

    const commandBarButtonStyles: IButtonStyles = {
        root: styles.calendarPickerButton,
        label: styles.calendarPickerLabel,
        textContainer: { maxWidth: calendarPickerMaxWidth },
    };

    const renderMenuList = React.useCallback(
        (menuListProps, defaultRender) => {
            return (
                <div>
                    <div
                        className={styles.defaultRenderContainer}
                        style={{
                            maxHeight: `${calloutMaxHeight - contextMenuItemHeight}px`,
                        }}>
                        {defaultRender(menuListProps)}
                    </div>
                    <DefaultButton
                        style={{
                            height: `${contextMenuItemHeight}px`,
                        }}
                        styles={{
                            label: styles.calendarPickerAddCalendarLabel,
                            root: styles.calendarPickerAddCalendarButton,
                        }}
                        text={loc(addCalendarLabel)}
                        title={loc(addCalendarLabel)}
                        onClick={onOpenAddCalendar}
                    />
                </div>
            );
        },
        [onOpenAddCalendar]
    );

    const titleProps: CalendarTitleProps = {
        title: title,
        setTitle: onChangeTitle,
    };

    const titleSection = getTitleSection(titleProps);
    pickerItems.unshift(titleSection);

    return (
        <div ref={containerRef} className={styles.calendarPickerContainer} onWheel={onWheel}>
            <div ref={calendarPickerRef} style={{ maxWidth: calendarPickerMaxWidth }}>
                <CommandBarButton
                    componentRef={pickerComponentRef}
                    styles={commandBarButtonStyles}
                    iconProps={commandBarButtonIconProps}
                    text={pickerLabel}
                    menuProps={{
                        items: pickerItems,
                        onRenderMenuList: renderMenuList,
                        ...commandBarButtonMenuProps,
                    }}
                />
            </div>

            {shouldShowNewButton && (
                <div className={styles.newEventButtonContainer} ref={newEventButtonRef}>
                    <DefaultButton
                        className={styles.newEventButton}
                        title={loc(calendarAssetNewEventButtonText)}
                        iconProps={{ iconName: ControlIcons.Add }}
                        onClick={newEventOnClick}
                        id={newEventButtonAnchorId}
                        styles={
                            !shouldShowNewEventText
                                ? { root: { width: '32px !important' } }
                                : undefined
                        }>
                        {shouldShowNewEventText && loc(calendarAssetNewEventButtonText)}
                    </DefaultButton>
                </div>
            )}
            {showSpinner && (
                <Spinner className={styles.calendarPickerSpinner} size={SpinnerSize.medium} />
            )}
        </div>
    );
});
